/**
 * useAuth — hook de autenticação dual-mode.
 *
 * Expõe três operações principais e dois selectors derivados:
 *
 *  • `login(email, password)` — orquestra os dois fluxos da spec §1:
 *      - email === 'admin'  → modo Basic (admin global). Não chama /me, não
 *        resolve tenant (admin global é tenant-agnóstico).
 *      - caso contrário      → modo JWT. login → /me → resolve tenant ativo
 *        → navega para `/`.
 *
 *  • `logout()` — limpa tudo: chama `/auth/logout` (best-effort), zera os
 *    stores de auth/tenant, limpa o cache do TanStack Query e navega para
 *    `/login`. É idempotente e nunca lança.
 *
 *  • `isAuthenticated` — true se há *algum* modo ativo (jwt OU basic).
 *  • `isAdmin`         — true se o modo ativo é `basic`.
 *  • `user`            — dados do `/me`, somente populado no modo JWT.
 *
 * As regras de detecção do admin global e o fluxo JWT seguem spec §1
 * ("Dois modos de autenticação") e §6 ("Fluxo JWT").
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { authService } from '@/services/auth.service'
import { useAuthStore } from '@/store/auth.store'
import { useTenantStore } from '@/store/tenant.store'
import type { MeResponse, Tenant } from '@/types/api'

import { resolveInitialTenant, tenantsFromMeResponse, userFromMeResponse } from './useInitializeApp'

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export interface UseAuthResult {
  /** Executa o fluxo de login apropriado (detecta admin global por email). */
  login: (email: string, password: string) => Promise<void>
  /** Encerra a sessão e redireciona para /login. Nunca lança. */
  logout: () => Promise<void>
  /** True se há sessão ativa (modo jwt OU basic). */
  isAuthenticated: boolean
  /** True somente se o modo ativo é `basic` (admin global). */
  isAdmin: boolean
  /** Dados do `/me`. Somente populado no modo JWT. */
  user: ReturnType<typeof useAuthStore.getState>['user']
}

// ---------------------------------------------------------------------------
// Constante reutilizada
// ---------------------------------------------------------------------------

/** Email reservado que dispara o modo HTTP Basic (admin global). */
export const ADMIN_GLOBAL_EMAIL = 'admin'

/** True se o email informado deve ativar o modo Basic (admin global). */
export function isAdminGlobalEmail(email: string): boolean {
  return email.trim().toLowerCase() === ADMIN_GLOBAL_EMAIL
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): UseAuthResult {
  const mode = useAuthStore((s) => s.mode)
  const user = useAuthStore((s) => s.user)

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  /* --------------------------- login ------------------------------------ */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Modo admin global: o frontend detecta pelo email reservado e
      // simplesmente armazena as credenciais HTTP Basic. NÃO chama /me
      // (admin global é tenant-agnóstico) e NÃO resolve tenant.
      if (isAdminGlobalEmail(email)) {
        useAuthStore.getState().setBasicAuth(email, password)
        // Limpa qualquer tenant residual de uma sessão JWT anterior no
        // mesmo navegador.
        useTenantStore.getState().clearTenant()
        // Limpa cache de queries — uma sessão Basic anterior pode ter
        // populado /admin/*, e queries tenant-escopadas certamente não
        // se aplicam a admin.
        queryClient.clear()
        navigate('/admin/organizations', { replace: true })
        return
      }

      // Modo JWT — fluxo completo.
      const tokens = await authService.login(email, password)
      useAuthStore.getState().setJwtAuth(tokens)

      // Após autenticado, GET /me popula user + accessible_tenants.
      const me: MeResponse = await authService.getMe()
      useAuthStore.getState().setUser(userFromMeResponse(me))

      const tenants: Tenant[] = tenantsFromMeResponse(me)
      useTenantStore.getState().setAccessibleTenants(tenants)

      const initialTenant = resolveInitialTenant(tenants, me.primary_tenant_id)
      if (initialTenant) {
        useTenantStore.getState().setActiveTenant(initialTenant)
      }

      // Cache limpo: qualquer query anterior (sessão de outro usuário no
      // mesmo navegador) deve ser descartada.
      queryClient.clear()

      navigate('/', { replace: true })
    },
    [navigate, queryClient],
  )

  /* --------------------------- logout ----------------------------------- */
  const logout = useCallback(async (): Promise<void> => {
    const { mode: currentMode, refreshToken } = useAuthStore.getState()

    // Best-effort: avisa o backend para revogar o refresh token.
    // Apenas no modo JWT — admin Basic não tem token para revogar.
    if (currentMode === 'jwt' && refreshToken) {
      try {
        await authService.logout(refreshToken)
      } catch {
        // Logout deve sempre prosseguir mesmo se a chamada falhar
        // (rede offline, token já expirado, etc.).
      }
    }

    // Limpa todo o estado client-side.
    useAuthStore.getState().clearAuth()
    useTenantStore.getState().clearTenant()
    // `clear()` remove cache + cancela queries em vôo. Mais drástico que
    // `removeQueries()`, mas é o que queremos na saída.
    queryClient.clear()

    navigate('/login', { replace: true })
  }, [navigate, queryClient])

  return {
    login,
    logout,
    isAuthenticated: mode !== null,
    isAdmin: mode === 'basic',
    user,
  }
}
